'use strict'

// Save checks for a column. The designer edits them as a rule chain. The
// server and the live form both call this file, so a block means the same
// thing on a form, a cell, the grid, the API, and a workflow.

const { matchesDisplayCondition, operatorsForDataType, isBlankOperator } = require('./display-rules')
const { columnSupportsRequired, isEmptyColumnValue, visitFormFields } = require('./column-write')
const { passwordPolicyFailure } = require('./password-policy')

function dataTypeForColumn (column) {

  if (column && (column.type === 'number' || column.type === 'duration' || column.type === 'sequence')) return 'number'
  if (column && column.type === 'dateTime') return 'datetime'
  if (column && column.type === 'signature') return 'signature'

  return 'string'
}

function validationOperators (dataType) {

  return operatorsForDataType(dataType).concat([{ key: 'taken', label: 'is already used' }])
}

function patternCompiles (pattern) {

  try {

    RegExp(String(pattern == null ? '' : pattern))

    return true
  } catch (err) {

    return false
  }
}

function normalizeCondition (condition) {

  const source = condition && condition.source === 'field' ? 'field' : 'self'
  const valueSource = condition && condition.valueSource === 'field' ? 'field' : 'literal'
  const operator = condition && condition.operator ? String(condition.operator) : '='

  return {
    source,
    field: source === 'field' ? String(condition.field || '') : '',
    operator,
    dataType: condition && condition.dataType ? String(condition.dataType) : '',
    valueSource,
    valueField: valueSource === 'field' ? String(condition.valueField || '') : '',
    value: valueSource === 'field' ? '' : (condition ? condition.value : '')
  }
}

function normalizeValidationRules (input) {

  const source = input && Array.isArray(input.rules) ? input.rules : (Array.isArray(input) ? input : [])

  return {
    rules: source.filter((rule) => rule && typeof rule === 'object').map((rule, index) => ({
      id: rule.id ? String(rule.id) : `rule_${index + 1}`,
      type: rule.type === 'elseIf' || rule.type === 'else' ? rule.type : 'if',
      conditionMatchMode: rule.conditionMatchMode === 'any' ? 'any' : 'all',
      conditions: rule.type === 'else' || !Array.isArray(rule.conditions) ? [] : rule.conditions.map(normalizeCondition),
      action: rule.action === 'allow' ? 'allow' : 'block',
      message: typeof rule.message === 'string' ? rule.message.trim() : ''
    }))
  }
}

function columnRules (column) {

  const stored = column && column.validationRules

  if (!stored) return []

  return normalizeValidationRules(stored).rules
}

function ruleUsesTaken (column) {

  return columnRules(column).some((rule) => (rule.conditions || []).some((condition) => condition.operator === 'taken'))
}

function columnsNeedingTakenCheck (columns) {

  return (columns || []).filter((column) => column && column.uniqueTitle && (column.unique || ruleUsesTaken(column)))
}

function readSubject (condition, column, values) {

  if (condition.source === 'field' && condition.field) return values ? values[condition.field] : undefined

  return values ? values[column.uniqueTitle] : undefined
}

function readExpected (condition, values) {

  if (condition.valueSource === 'field' && condition.valueField) return values ? values[condition.valueField] : undefined

  return condition.value
}

function conditionResult (condition, column, values, context) {

  if (!condition) return { matched: false }

  if (condition.operator === 'taken') {

    const taken = context && context.taken

    return { matched: !!(taken && taken[column.uniqueTitle]) }
  }

  const expected = readExpected(condition, values)

  if ((condition.operator === 'matches' || condition.operator === 'notMatches') && condition.valueSource !== 'field' && !patternCompiles(expected)) {

    return { matched: false, invalidPattern: true }
  }

  const subject = readSubject(condition, column, values)
  const dataType = condition.dataType || dataTypeForColumn(condition.source === 'self' ? column : null)
  const blankSubject = isEmptyColumnValue(subject)

  if (blankSubject && condition.operator !== 'empty' && condition.operator !== 'notEmpty' && condition.operator !== 'taken') {

    return { matched: condition.operator === 'notMatches' }
  }

  return { matched: matchesDisplayCondition(subject, condition.operator, expected, dataType) }
}

function ruleResult (rule, column, values, context) {

  if (!rule) return { matched: false }
  if (rule.type === 'else') return { matched: true }

  const list = rule.conditions || []

  if (!list.length) return { matched: true }

  const results = list.map((condition) => conditionResult(condition, column, values, context))

  if (results.some((result) => result.invalidPattern)) return { matched: true, invalidPattern: true }

  const matched = rule.conditionMatchMode === 'any'
    ? results.some((result) => result.matched)
    : results.every((result) => result.matched)

  return { matched }
}

function columnLabel (column) {

  return (column && (column.title || column.uniqueTitle)) || 'This value'
}

function matchingRule (column, values, context) {

  for (const rule of columnRules(column)) {

    const result = ruleResult(rule, column, values, context)

    if (result.invalidPattern) return { rule, invalidPattern: true }
    if (result.matched) return { rule }
  }

  return null
}

function columnFailure (column, values, context, options) {

  if (!column || !column.uniqueTitle) return null

  const only = options && options.only

  if (only && !only.has(column.uniqueTitle)) return null

  const skip = options && options.skip

  if (skip && skip.has(column.uniqueTitle)) return null

  const match = matchingRule(column, values, context)

  if (match && match.invalidPattern) {

    return `The pattern on ${columnLabel(column)} is not a valid regular expression.`
  }

  if (match && match.rule.action !== 'allow') {

    return match.rule.message || `${columnLabel(column)} is not valid`
  }

  const value = values ? values[column.uniqueTitle] : undefined

  if (column.type === 'password' && context && context.passwordPolicy) {

    const extra = context.passwordChecks && context.passwordChecks[column.uniqueTitle]
    const policyMessage = passwordPolicyFailure(value, context.passwordPolicy, extra)

    if (policyMessage) return policyMessage
  }

  if (column.required && columnSupportsRequired(column) && isEmptyColumnValue(value, column)) {

    return `${columnLabel(column)} is required`
  }

  const taken = context && context.taken && context.taken[column.uniqueTitle]
  const allowCoversTaken = !!(match && match.rule.action === 'allow' && (match.rule.conditions || []).some((condition) => condition.operator === 'taken'))

  if (column.unique && taken && !allowCoversTaken) {

    return uniqueFailureMessage(column, value, taken === 'batch')
  }

  return null
}

function uniqueFailureMessage (column, value, batch) {

  const title = column.title || column.uniqueTitle
  const shown = value == null ? '' : value

  if (batch) return `duplicate value for unique field "${title}": "${shown}" appears multiple times in the batch`

  return `duplicate value for unique field "${title}": "${shown}" already exists`
}

function recordFailures (columns, values, context, options) {

  const failures = []

  for (const column of columns || []) {

    const message = columnFailure(column, values, context, options)

    if (!message) continue

    failures.push({
      uniqueTitle: column.uniqueTitle,
      title: columnLabel(column),
      message
    })
  }

  return failures
}

function fieldsAsColumns (components) {

  const columns = []

  visitFormFields(components, (field) => {

    columns.push({
      uniqueTitle: field.uniqueTitle,
      title: field.title || field.uniqueTitle,
      type: field.columnType || 'text',
      required: !!field.required,
      validationRules: field.validationRules || { rules: [] }
    })
  })

  return columns
}

module.exports = {
  dataTypeForColumn,
  validationOperators,
  isBlankOperator,
  patternCompiles,
  normalizeValidationRules,
  ruleUsesTaken,
  columnsNeedingTakenCheck,
  recordFailures,
  fieldsAsColumns
}
